/**
 * REFORM Pulse — central analytics module (GA4 + Consent Mode v2, basic consent)
 *
 * Guarantees:
 *  - gtag.js is NEVER fetched before Analytics consent is granted.
 *  - Consent defaults to denied (analytics/ads all denied).
 *  - Admin/internal routes are excluded from tracking + script load.
 *  - `pulse.disableAnalytics` localStorage flag = '1' fully disables analytics.
 *  - Only approved, non-PII event params are forwarded to GA4.
 *  - Dedup helpers prevent double page_view / form_start / generate_lead.
 *
 * The base `<script>` in public/index.html only sets `window.dataLayer` and
 * a local `gtag()` stub plus Consent Mode v2 defaults. This module is the
 * ONLY place that dynamically injects the external gtag.js script.
 */

export const GA4_MEASUREMENT_ID = "G-6NVW5FSDWX";

export const CONSENT_STORAGE_KEY = "pulse.analyticsConsent"; // 'granted' | 'denied' | null (undecided)
export const OPT_OUT_STORAGE_KEY = "pulse.disableAnalytics"; // '1' to hard-disable

// Route prefixes that must never be tracked and must never trigger script load.
export const EXCLUDED_ROUTE_PREFIXES = ["/admin", "/internal"];

// Allowed non-PII parameter keys per event. Any key not listed here is
// stripped before being forwarded to GA4. This is a hard privacy safeguard.
const EVENT_PARAM_ALLOWLIST = {
    view_project: ["project_slug", "project_number", "source"],
    play_video: ["video_id", "placement", "source"],
    start_brief: ["placement", "source", "target"],
    form_start: ["form_name", "source_page"],
    generate_lead: ["event_type", "budget_range", "source_page", "form_name"],
    contact_whatsapp: ["placement", "source_page"],
    contact_email: ["placement", "source_page"],
    page_view: ["page_location", "page_path", "page_title"],
};

// Consent-changed custom event name so any listener (e.g. App.js) can
// re-render or trigger a first page_view when consent flips to granted.
export const CONSENT_CHANGE_EVENT = "pulse:consent-changed";
export const OPEN_COOKIE_PREFS_EVENT = "pulse:open-cookie-prefs";

function safeStorageGet(key) {
    try {
        if (typeof window === "undefined" || !window.localStorage) return null;
        return window.localStorage.getItem(key);
    } catch (_e) {
        return null;
    }
}

function safeStorageSet(key, value) {
    try {
        if (typeof window === "undefined" || !window.localStorage) return;
        window.localStorage.setItem(key, value);
    } catch (_e) {
        // ignore quota / privacy-mode errors
    }
}

function safeSessionGet(key) {
    try {
        if (typeof window === "undefined" || !window.sessionStorage) return null;
        return window.sessionStorage.getItem(key);
    } catch (_e) {
        return null;
    }
}

function safeSessionSet(key, value) {
    try {
        if (typeof window === "undefined" || !window.sessionStorage) return;
        window.sessionStorage.setItem(key, value);
    } catch (_e) {
        // ignore
    }
}

/** Return true if the current visitor has hard-disabled analytics locally. */
export function isOptedOut() {
    return safeStorageGet(OPT_OUT_STORAGE_KEY) === "1";
}

/** Return current analytics consent state: 'granted' | 'denied' | null. */
export function getConsentState() {
    const v = safeStorageGet(CONSENT_STORAGE_KEY);
    if (v === "granted" || v === "denied") return v;
    return null;
}

/** Return true only if consent === 'granted' AND not opted out. */
export function isAnalyticsEnabled() {
    if (isOptedOut()) return false;
    return getConsentState() === "granted";
}

/** Return true if a given pathname is trackable (not admin/internal). */
export function isTrackablePath(pathname) {
    if (!pathname || typeof pathname !== "string") return false;
    return !EXCLUDED_ROUTE_PREFIXES.some((prefix) =>
        pathname === prefix || pathname.startsWith(`${prefix}/`) || pathname.startsWith(`${prefix}?`)
    );
}

/** True only when the current live location is trackable. */
function currentPathTrackable() {
    try {
        return isTrackablePath(window.location.pathname);
    } catch (_e) {
        return false;
    }
}

function ensureDataLayer() {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    // gtag stub is already defined by index.html, but re-declare defensively.
    if (typeof window.gtag !== "function") {
        window.gtag = function () {
            window.dataLayer.push(arguments);
        };
    }
}

/** Emit a browser custom event so React components can react to state changes. */
function emitConsentChanged(detail) {
    try {
        if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
        window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail }));
    } catch (_e) {
        // ignore
    }
}

let gtagScriptRequested = false;

/**
 * Dynamically inject the external gtag.js script exactly once.
 * Called only after Analytics consent is granted and opt-out is off and route is trackable.
 */
function loadGtagScriptOnce() {
    if (typeof document === "undefined") return;
    if (gtagScriptRequested) return;
    if (document.getElementById("ga4-gtag")) {
        gtagScriptRequested = true;
        return;
    }
    gtagScriptRequested = true;
    const s = document.createElement("script");
    s.id = "ga4-gtag";
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_MEASUREMENT_ID)}`;
    document.head.appendChild(s);

    ensureDataLayer();
    window.gtag("js", new Date());
    // Manual SPA page views — disable auto page_view from config so we can
    // fire one per client-side route change from AnalyticsRouteListener.
    window.gtag("config", GA4_MEASUREMENT_ID, { send_page_view: false });
}

/**
 * Boot-time helper: called once from App.js on mount. Re-asserts the visitor's
 * previously-stored consent choice against the runtime gtag state, and (if
 * consent was previously granted) triggers gtag.js load exactly once.
 *
 * IMPORTANT: this function does NOT load gtag.js unless the visitor has
 * explicitly granted analytics consent in a prior session. It respects the
 * opt-out flag and the current route.
 */
export function bootAnalytics() {
    try {
        ensureDataLayer();
        if (isOptedOut()) {
            // Ensure denied state is asserted even if the base tag was
            // somehow altered by a third-party integration.
            window.gtag("consent", "update", {
                analytics_storage: "denied",
                ad_storage: "denied",
                ad_user_data: "denied",
                ad_personalization: "denied",
            });
            return;
        }
        const stored = getConsentState();
        if (stored === "granted") {
            // Re-assert granted signal and lazily load gtag.js on public routes.
            window.gtag("consent", "update", {
                analytics_storage: "granted",
                ad_storage: "denied",
                ad_user_data: "denied",
                ad_personalization: "denied",
            });
            if (currentPathTrackable()) {
                loadGtagScriptOnce();
            }
        }
        // If stored === "denied" or null: do nothing. The base tag already
        // set default consent to denied and we must not load gtag.js.
    } catch (_e) {
        // never throw from analytics boot
    }
}

/**
 * Persist consent choice and update GA + Consent Mode v2 state accordingly.
 *
 * Rules:
 *  - When granting: only load gtag.js if opt-out is off AND current route is trackable.
 *  - When denying: do NOT reload the page; update consent to denied; future events won't send.
 *  - We update the consent signal via window.gtag(...) regardless of whether the
 *    external script is loaded — the stub simply queues the call safely.
 */
export function setAnalyticsConsent(state) {
    if (state !== "granted" && state !== "denied") return;
    ensureDataLayer();
    safeStorageSet(CONSENT_STORAGE_KEY, state);

    if (state === "granted") {
        // Update Consent Mode v2 signal first so any queued events afterwards are honoured.
        window.gtag("consent", "update", {
            analytics_storage: "granted",
            ad_storage: "denied",
            ad_user_data: "denied",
            ad_personalization: "denied",
        });
        if (!isOptedOut() && currentPathTrackable()) {
            loadGtagScriptOnce();
        }
    } else {
        // Withdraw — keep site working, no reload, stop future analytics events.
        window.gtag("consent", "update", {
            analytics_storage: "denied",
            ad_storage: "denied",
            ad_user_data: "denied",
            ad_personalization: "denied",
        });
    }

    emitConsentChanged({ state, optedOut: isOptedOut() });
}

/** Convenience: withdraw consent (equivalent to setAnalyticsConsent('denied')). */
export function withdrawAnalyticsConsent() {
    setAnalyticsConsent("denied");
}

/** Convenience: grant consent (equivalent to setAnalyticsConsent('granted')). */
export function grantAnalyticsConsent() {
    setAnalyticsConsent("granted");
}

/**
 * Filter event params against the per-event allowlist. Any key not listed is
 * dropped. Values are coerced to strings/numbers/booleans, no objects/arrays.
 */
function sanitizeEventParams(name, params) {
    const allowed = EVENT_PARAM_ALLOWLIST[name] || [];
    const out = {};
    if (!params || typeof params !== "object") return out;
    for (const key of allowed) {
        const value = params[key];
        if (value === undefined || value === null) continue;
        const t = typeof value;
        if (t === "string") {
            // Trim + hard length cap to avoid accidental long free-text payloads.
            out[key] = value.slice(0, 100);
        } else if (t === "number" || t === "boolean") {
            out[key] = value;
        }
    }
    return out;
}

/**
 * Core sender. Silently drops the call if analytics is disabled/opted out,
 * consent not granted, current route excluded, or event name unknown.
 */
export function trackEvent(name, params) {
    try {
        if (!name || typeof name !== "string") return;
        if (!(name in EVENT_PARAM_ALLOWLIST)) return; // unknown event — never send
        if (isOptedOut()) return;
        if (!isAnalyticsEnabled()) return;
        if (!currentPathTrackable()) return;
        if (typeof window === "undefined" || typeof window.gtag !== "function") return;
        // Extra safety: only dispatch to gtag if the actual script has been requested.
        // (If for any reason gtag.js failed to load, the stub still queues; that's fine.)
        const clean = sanitizeEventParams(name, params);
        window.gtag("event", name, clean);
    } catch (_e) {
        // never throw from analytics
    }
}

/**
 * Send a page_view manually. Deduped by caller (AnalyticsRouteListener keeps
 * a ref of the last key so this function does not track its own state).
 */
export function trackPageView({ page_location, page_path, page_title }) {
    trackEvent("page_view", { page_location, page_path, page_title });
}

// --- Convenience helpers for the seven business events -----------------------

export function trackViewProject({ project_slug, project_number, source }) {
    trackEvent("view_project", { project_slug, project_number, source });
}

export function trackPlayVideo({ video_id, placement, source }) {
    trackEvent("play_video", { video_id, placement, source });
}

export function trackStartBrief({ placement, source, target }) {
    trackEvent("start_brief", { placement, source, target });
}

/** Deduped once per session per form_name. */
export function trackFormStartOnce({ form_name = "pulse_brief", source_page } = {}) {
    const key = `ga4.form_start.${form_name}`;
    if (safeSessionGet(key) === "1") return;
    safeSessionSet(key, "1");
    trackEvent("form_start", { form_name, source_page });
}

export function trackGenerateLead({ event_type, budget_range, source_page, form_name = "pulse_brief" }) {
    // Explicitly NO value / currency / revenue.
    trackEvent("generate_lead", { event_type, budget_range, source_page, form_name });
}

export function trackContactWhatsApp({ placement, source_page } = {}) {
    trackEvent("contact_whatsapp", { placement, source_page });
}

export function trackContactEmail({ placement, source_page } = {}) {
    trackEvent("contact_email", { placement, source_page });
}

// --- UI helpers -------------------------------------------------------------

/** Open the preferences dialog from anywhere in the app (e.g. footer link). */
export function openCookiePreferences() {
    try {
        if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
        window.dispatchEvent(new CustomEvent(OPEN_COOKIE_PREFS_EVENT));
    } catch (_e) {
        // ignore
    }
}

/**
 * Small helper for debugging in preview only. Not called anywhere in prod code.
 * Users are documented to invoke `localStorage.setItem('pulse.disableAnalytics','1')`
 * directly per requirements; this is a convenience alias.
 */
export function enableLocalOptOut() {
    safeStorageSet(OPT_OUT_STORAGE_KEY, "1");
    // If gtag is present, also flip consent denied so any queued calls stop.
    try {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
            window.gtag("consent", "update", {
                analytics_storage: "denied",
                ad_storage: "denied",
                ad_user_data: "denied",
                ad_personalization: "denied",
            });
        }
    } catch (_e) {
        // ignore
    }
    emitConsentChanged({ state: getConsentState(), optedOut: true });
}
